import {
  Box,
  Container,
  Grid,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { EmptyStateIcon, Filter } from "assets/Icon";
import { useEffect, useState } from "react";
import useDispatch from "hooks/useDispatch";
import useIsMobileView from "hooks/useIsMobileView";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { getRequestHistoryForm, getRequestHistoryList } from "store/slices/wallet/walletSlice";
import RequestHistoryFilters from "./RequestHistoryFilters";
import Pagination from "components/Pagination";
import { formatDateToJalali } from "utils/helpers/time";
import RequestHistoryBox from "./components/RequestHistoryBox";
import TableSkeleton from "components/Skeletons/Table.skeleton";
import Table from "components/Table";
import { INVESTMENT_HISTORY_HEAD } from "./constants";
import EmptyState from "components/EmptyState";
import NumberFormats from "components/NumberFormats";
import WrapperAmount from "../Basket/components/TableBasketHistory/Component/WrapperAmount";
import BackButton from "components/BackButton";

function RequestHistory() {
  const isMobileView = useIsMobileView();
  const dispatch = useDispatch();
  const theme = useTheme();

  const requestHistoryList = useSelector(
    (state) => state.wallet.requestHistoryList
  );

  const [searchParams] = useSearchParams();

  const page = +searchParams.get("page") || 1;
  const perPage = +searchParams.get("perPage") || 10;
  const basketId = +searchParams.get("basketId") || -1;
  const startDate = searchParams.get("startDate") || "";
  const endDate = searchParams.get("endDate") || "";
  const coinId = +searchParams.get("coinId") || -1;
  const sideId = +searchParams.get("sideId") || 0;
  const stateId = +searchParams.get("stateId") || 0;
  const typeId = +searchParams.get("typeId") || 0;
  const forId = +searchParams.get("forId") || 0;
  const userName = searchParams.get("userName") || "";

  const [showFilter, setShowFilter] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleGetRequestHistory = async ({
    page,
    perPage,
  }) => {
    setLoading(true);

    await dispatch(
      getRequestHistoryList({
        startDate: formatDateToJalali(startDate),
        endDate: formatDateToJalali(endDate),
        coinId,
        sideId,
        pageSize: perPage,
        pageNumber: page,
        basketId,
        stateId,
        typeId,
        forId,
        userName
      })
    );

    setLoading(false);
  };

  const handleChangePaginate = ({ page, perPage }) => {
    handleGetRequestHistory({ page, perPage });
  };

  useEffect(() => {
    handleGetRequestHistory({ page, perPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    basketId,
    startDate,
    endDate,
    coinId,
    sideId,
    stateId,
    typeId,
    forId,
    userName
  ]);

  useEffect(() => {
    dispatch(getRequestHistoryForm());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formattedItems = requestHistoryList?.data?.map((item) => ({
    ...item,
    coin: <WrapperAmount data={item} />,
    portfolioOwner: item?.portfolioOwner,
    fee: (
      <Typography color="text.main" fontWeight={500} variant="h6">
        <NumberFormats value={item?.fee} />{" "}
        <Typography
          component="span"
          color="text.secondary"
          fontWeight={400}
          variant="subtitle1"
        >
          {item?.coinName}
        </Typography>
      </Typography>
    ),
    side: item?.side,
    state: item?.state,
    amount: (
      <Typography color="text.main" fontWeight={500} variant="h6">
        <NumberFormats value={item?.amount} />{" "}
        <Typography
          component="span"
          color="text.secondary"
          fontWeight={400}
          variant="subtitle1"
        >
          {item?.coinName}
        </Typography>
      </Typography>
    ),
    date: (
      <>
        {item?.date}/{item?.time}
      </>
    ),
    dateTimeEdit: (
      <>
        {item?.dateEdit}/{item?.timeEdit}
      </>
    ),
    comment: item?.comment,
  }));

  return (
    <Container maxWidth="100%">
      <Grid container alignItems="center">
        <Grid item xs={12}>
        </Grid>

        <Grid item xs={6}>
          <Box sx={{ display: { md: 'none', xs: 'block' } }}>
            <BackButton text="Request History" to="/wallet" />
          </Box>

          <Typography sx={{ display: { md: 'block', xs: 'none' } }} color="text.main" variant="h4">Request History</Typography>
        </Grid>

        <Grid item xs={6} textAlign="right">
          <IconButton
            onClick={() => {
              setShowFilter((v) => !v);
            }}
            aria-label="filter"
          >
            <Filter color={theme.palette.text.main} />
          </IconButton>
        </Grid>
      </Grid>

      {showFilter && <RequestHistoryFilters />}

      <Grid container mt={2} spacing={2}>
        <>
          {isMobileView ? (
            <>
              {requestHistoryList?.data?.length ? (
                <>
                  {requestHistoryList?.data?.map((investment) => (
                    <Grid item xs={12} sm={6}>
                      <RequestHistoryBox item={investment} />
                    </Grid>
                  ))}
                </>
              ) : (
                <Grid item xs={12}>
                  <EmptyState
                    text="No History"
                    icon={
                      <EmptyStateIcon color={theme.palette.text.secondary} />
                    }
                  />
                </Grid>
              )}
            </>
          ) : (
            <>
              {!loading ? (
                <Grid item xs={12}>
                  <Table
                    headers={INVESTMENT_HISTORY_HEAD}
                    items={formattedItems}
                  />
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <TableSkeleton />
                </Grid>
              )}
            </>
          )}
        </>

        <Grid item xs={12}>
          <Pagination
            perPage={requestHistoryList?.pageSize}
            count={requestHistoryList?.totalPages}
            onChange={handleChangePaginate}
          />
        </Grid>
      </Grid>
    </Container>
  );
}

export default RequestHistory;
